<template>
  <div class="ordine-asporto">
    <!-- Header -->
    <div class="header">
      <a href="#" class="btn-back" @click.prevent="goBack">← Dashboard</a>
      <h1>{{ $t('client_asporto') }}</h1>
    </div>

    <!-- Customer Info Form -->
    <div class="customer-form" v-if="!showMenu">
      <h2>{{ $t('nuovo_asporto_dati') }}</h2>
      <div class="form-group">
        <label for="nome">{{ $t('nome') }} *</label>
        <input 
          type="text" 
          id="nome" 
          v-model="customerInfo.nome" 
          :placeholder="$t('nome')"
          required
        />
      </div>
      <div class="form-group">
        <label for="email">Email *</label>
        <input 
          type="email" 
          id="email" 
          v-model="customerInfo.email" 
          placeholder="Email"
          required
        />
      </div>
      <div class="form-group">
        <label for="telefono">{{ $t('telefono') }} *</label>
        <input 
          type="tel" 
          id="telefono" 
          v-model="customerInfo.telefono" 
          :placeholder="$t('telefono')"
          required
        />
      </div>
      <div class="form-group">
        <label for="dataRitiro">{{ $t('data') }} {{ $t('ritiro') }} *</label>
        <input 
          type="date" 
          id="dataRitiro" 
          v-model="customerInfo.dataRitiro" 
          :min="minDate"
          required
        />
      </div>
      <div class="form-group">
        <label for="oraRitiro">{{ $t('ora_ritiro_asterisk') || 'Ora ritiro *' }}</label>
        
        <div class="time-range-info my-2" v-if="customerInfo.dataRitiro">
          <div v-if="!isDayAsportoActive" class="text-danger fw-bold small">
            ❌ L'asporto non è attivo il {{ getGiornoName(dayOfWeekOfSelectedDate) }}.
          </div>
          <div v-else-if="currentDayAsportoSettings && hasTimeRangesForSelectedDate" class="small text-muted">
            <strong>Orari disponibili per {{ getGiornoName(dayOfWeekOfSelectedDate) }}:</strong>
            <div class="mt-1 d-flex flex-wrap gap-2">
              <span v-if="currentDayAsportoSettings.asportoPranzoStart" class="badge bg-light text-dark border">
                Pranzo: {{ currentDayAsportoSettings.asportoPranzoStart }} - {{ currentDayAsportoSettings.asportoPranzoEnd }}
              </span>
              <span v-if="currentDayAsportoSettings.asportoCenaStart" class="badge bg-light text-dark border">
                Cena: {{ currentDayAsportoSettings.asportoCenaStart }} - {{ currentDayAsportoSettings.asportoCenaEnd }}
              </span>
            </div>
          </div>
        </div>

        <input 
          type="time" 
          id="oraRitiro" 
          v-model="customerInfo.oraRitiro" 
          :disabled="!isDayAsportoActive"
          required
        />
        <div v-if="customerInfo.oraRitiro && !isOraRitiroValida" class="text-danger small mt-1">
          ⚠️ L'orario selezionato non ricade nelle fasce orarie consentite per questo giorno.
        </div>
      </div>
      <button class="btn-primary" @click="proceedToMenu" :disabled="!isFormValid">
        {{ $t('conferma') }}
      </button>
    </div>

    <!-- Menu Selection -->
    <div class="menu-section" v-if="showMenu">
      <div class="menu-header">
        <h2>{{ $t('scegli_piatti') }}</h2>
        <span class="cart-badge" @click="showCart = !showCart">
          🛒 {{ $t('carrello') }} ({{ ordine.length }})
        </span>
      </div>

      <!-- Category Filter Bar -->
      <div class="category-filter-bar" v-if="availableCategories.length > 1">
        <button 
          v-for="cat in availableCategories" 
          :key="cat.id"
          class="cat-btn"
          :class="{ active: selectedCategory === cat.id }"
          @click="selectedCategory = cat.id"
        >
          {{ cat.label }}
        </button>
      </div>

      <!-- Cart Overlay -->
      <div class="cart-overlay" v-if="showCart">
        <div class="cart-content">
          <h3>{{ $t('ordinazione') }}</h3>
          <div v-if="ordine.length === 0" class="cart-empty">
            {{ $t('carrello_vuoto') }}
          </div>
          <div v-else>
            <div class="cart-item" v-for="(item, idx) in ordine" :key="idx">
              <div class="cart-item-info">
                <div>
                  <span class="cart-item-name">{{ item.piatto }}</span>
                  <div class="cart-item-modifications" v-if="item.aggiunta.length || item.senza.length">
                    <small v-if="item.aggiunta.length" class="text-success">+ {{ item.aggiunta.map(a => a.descrizione || a).join(', ') }}</small>
                    <br v-if="item.aggiunta.length && item.senza.length">
                    <small v-if="item.senza.length" class="text-danger">- {{ item.senza.join(', ') }}</small>
                  </div>
                  <div class="cart-item-note-input mt-1">
                    <input 
                      type="text" 
                      v-model="item.nota" 
                      class="form-control form-control-sm" 
                      style="background: rgba(255, 255, 255, 0.15); color: white; border: 1px solid rgba(255, 255, 255, 0.25); border-radius: 6px; padding: 4px 8px; font-size: 0.85rem;"
                      :placeholder="$t('scrivi_nota', 'Aggiungi / modifica nota...')"
                    />
                  </div>
                </div>
                <span class="cart-item-qty">x{{ item.quantita }}</span>
                <span class="cart-item-price">{{ (item.prezzo * item.quantita).toFixed(2) }}€</span>
              </div>
              <button class="btn-remove" @click="removeItem(idx)">✕</button>
            </div>
            <div class="cart-total">
              <strong>{{ $t('totale') }}: {{ totale.toFixed(2) }}€</strong>
            </div>
            <button v-if="!isStaff" class="btn-pay" @click="proceedToPayment" :disabled="isProcessing">
              {{ isProcessing ? '...' : $t('paga_stripe') }}
            </button>
            <div v-else class="staff-actions">
              <button class="btn-pay btn-staff-pay" @click="proceedToCreateOrder(true)" :disabled="isProcessing">
                {{ isProcessing ? '...' : $t('crea_ordine_pagato') }}
              </button>
              <button class="btn-pay btn-staff-pending" @click="proceedToCreateOrder(false)" :disabled="isProcessing">
                {{ isProcessing ? '...' : $t('crea_ordine_da_pagare') }}
              </button>
            </div>
          </div>
          <button class="btn-close-cart" @click="showCart = false">{{ $t('exit') }}</button>
        </div>
      </div>

      <!-- Variations Modal -->
      <div class="modal-overlay" v-if="showVariationModal">
        <div class="modal-content">
          <h3>{{ $t('personalizza_aspetto') }} {{ currentDish.nome }}</h3>
          
          <div class="variation-section" v-if="currentDish.addProd && currentDish.addProd.length">
            <h4>{{ $t('aggiunte') }}</h4>
            <div class="variation-grid">
              <label v-for="add in currentDish.addProd" :key="add.prodottoId || add" class="variation-item">
                <input type="checkbox" :value="add" v-model="selectedAggiunte">
                <span>{{ add.descrizione || add }}</span>
              </label>
            </div>
          </div>

          <div class="variation-section" v-if="currentDish.removeProd && currentDish.removeProd.length">
            <h4>{{ $t('senza') }}</h4>
            <div class="variation-grid">
              <label v-for="rem in currentDish.removeProd" :key="rem" class="variation-item">
                <input type="checkbox" :value="rem" v-model="selectedSenza">
                <span>{{ rem }}</span>
              </label>
            </div>
          </div>

          <div class="variation-section">
            <h4>{{ $t('nota', 'Nota') }}</h4>
            <textarea v-model="notaModal" class="form-control" rows="2" style="background: rgba(255, 255, 255, 0.1); color: white; border: 1px solid rgba(255, 255, 255, 0.2); width: 100%; border-radius: 8px; padding: 10px;" :placeholder="$t('nota', 'Nota')"></textarea>
          </div>

          <div class="modal-actions">
            <button class="btn-secondary" @click="showVariationModal = false">{{ $t('annulla') }}</button>
            <button class="btn-primary" @click="confirmVariations">{{ $t('conferma') }}</button>
          </div>
        </div>
      </div>

      <!-- Dynamic Menu Categories -->
      <div class="menu-categories">
        <div 
          class="category" 
          v-for="(dishes, categoryId) in filteredPiattiPerPortata" 
          :key="categoryId"
        >
          <h3>{{ formatCategoryLabel(categoryId) }}</h3>
          <div class="dishes-grid">
            <div class="dish-card" v-for="dish in dishes" :key="dish.nome">
              <h4>{{ getTranslatedName(dish) }}</h4>
              <div class="description-container" :class="{ expanded: dish.expanded }">
                <p>{{ getTranslatedDesc(dish) }}</p>
                <div v-if="getAllergeni(dish)" class="allergeni-info">
                  <strong>{{ $t('allergeni') }}:</strong> {{ getAllergeni(dish) }}
                </div>
              </div>
              <div class="dish-footer">
                <span class="price">{{ dish.prezzo }}€</span>
                <div class="dish-actions" style="display: flex; flex-direction: column; gap: 10px; width: 100%;">
                  <input type="text" v-model="dish.nota" class="form-control form-control-sm" style="background: rgba(255, 255, 255, 0.1); color: white; border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 6px; padding: 6px 10px;" :placeholder="$t('nota', 'Nota')">
                  <div style="display: flex; justify-content: flex-end; gap: 10px;">
                    <button v-if="dish.descrizione || getAllergeni(dish)" class="btn-info-toggle" @click="toggleDetails(dish)" :title="$t('dettagli')">
                      <i class="fas" :class="dish.expanded ? 'fa-chevron-up' : 'fa-info-circle'"></i>
                    </button>
                    <button class="btn-add" @click="addToCart(dish)">+ {{ $t('aggiungi') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Message -->
    <div class="error-message" v-if="errorMessage">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { store } from '../store/store.js'

export default {
  name: 'OrdineAsporto',
  data() {
    return {
      store,
      customerInfo: {
        nome: '',
        email: '',
        telefono: '',
        dataRitiro: '',
        oraRitiro: ''
      },
      showMenu: false,
      showCart: false,
      piatti: [],
      piattiPerPortata: {},
      ordine: [],
      isProcessing: false,
      errorMessage: '',
      showVariationModal: false,
      currentDish: null,
      selectedAggiunte: [],
      selectedSenza: [],
      notaModal: '',
      selectedCategory: 'all',
      predefinedCategories: [
        { id: 'antipasti', label: 'nostri_antipasti' },
        { id: 'primi', label: 'nostri_primi' },
        { id: 'secondi', label: 'nostri_secondi' },
        { id: 'poke', label: 'nostri_poke' },
        { id: 'contorni', label: 'nostri_contorni' },
        { id: 'pizza', label: 'nostra_pizza' },
        { id: 'teglia', label: 'nostra_teglia' },
        { id: 'trancio', label: 'nostro_trancio' },
        { id: 'focaccia', label: 'nostra_focaccia' },
        { id: 'insalate', label: 'nostre_insalate' },
        { id: 'combo', label: 'nostro_combo' },
        { id: 'sushi', label: 'nostro_sushi' },
        { id: 'dolci', label: 'nostri_dolci' },
        { id: 'bevande', label: 'nostre_bevande' }
      ]
    }
  },
  computed: {
    availableCategories() {
      const cats = [{ id: 'all', label: this.$t('tutti') }];
      
      this.predefinedCategories.forEach(p => {
        if (this.piattiPerPortata[p.id]) {
          cats.push({ id: p.id, label: this.$t(p.label) });
        }
      });
      
      return cats;
    },
    filteredPiattiPerPortata() {
      if (this.selectedCategory === 'all') return this.piattiPerPortata;
      const filtered = {};
      if (this.piattiPerPortata[this.selectedCategory]) {
        filtered[this.selectedCategory] = this.piattiPerPortata[this.selectedCategory];
      }
      return filtered;
    },
    dayOfWeekOfSelectedDate() {
      if (!this.customerInfo.dataRitiro) return null;
      const parts = this.customerInfo.dataRitiro.split('-');
      if (parts.length !== 3) return null;
      const yyyy = parseInt(parts[0], 10);
      const mm = parseInt(parts[1], 10) - 1;
      const dd = parseInt(parts[2], 10);
      const d = new Date(yyyy, mm, dd);
      return d.getDay();
    },
    currentDayAsportoSettings() {
      if (!this.asportoSettings) return null;
      const dow = this.dayOfWeekOfSelectedDate;
      if (dow !== null && dow !== undefined && this.asportoSettings.giorni) {
        const daySetting = this.asportoSettings.giorni[dow] || this.asportoSettings.giorni[String(dow)];
        if (daySetting) {
          return {
            attivo: daySetting.attivo !== false,
            asportoPranzoStart: daySetting.asportoPranzoStart || this.asportoSettings.asportoPranzoStart,
            asportoPranzoEnd: daySetting.asportoPranzoEnd || this.asportoSettings.asportoPranzoEnd,
            asportoCenaStart: daySetting.asportoCenaStart || this.asportoSettings.asportoCenaStart,
            asportoCenaEnd: daySetting.asportoCenaEnd || this.asportoSettings.asportoCenaEnd
          };
        }
      }
      return {
        attivo: true,
        asportoPranzoStart: this.asportoSettings.asportoPranzoStart,
        asportoPranzoEnd: this.asportoSettings.asportoPranzoEnd,
        asportoCenaStart: this.asportoSettings.asportoCenaStart,
        asportoCenaEnd: this.asportoSettings.asportoCenaEnd
      };
    },
    isDayAsportoActive() {
      const s = this.currentDayAsportoSettings;
      return s ? s.attivo : true;
    },
    hasTimeRangesForSelectedDate() {
      const s = this.currentDayAsportoSettings;
      if (!s || !s.attivo) return false;
      return !!(s.asportoPranzoStart || s.asportoCenaStart);
    },
    isOraRitiroValida() {
      if (!this.customerInfo.dataRitiro || !this.customerInfo.oraRitiro) return true;
      const s = this.currentDayAsportoSettings;
      if (!s) return true;
      if (!s.attivo) return false;

      const ora = this.customerInfo.oraRitiro;
      const isLunch = s.asportoPranzoStart && s.asportoPranzoEnd ?
        (ora >= s.asportoPranzoStart && ora <= s.asportoPranzoEnd) : false;
      const isDinner = s.asportoCenaStart && s.asportoCenaEnd ?
        (ora >= s.asportoCenaStart && ora <= s.asportoCenaEnd) : false;

      if (!s.asportoPranzoStart && !s.asportoPranzoEnd && !s.asportoCenaStart && !s.asportoCenaEnd) {
        return true;
      }

      return isLunch || isDinner;
    },
    isFormValid() {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const phoneRegex = /^[\d\s+\-()]{6,20}$/;
      return this.customerInfo.nome.trim() && 
             this.customerInfo.email.trim() &&
             emailRegex.test(this.customerInfo.email) &&
             this.customerInfo.telefono.trim() &&
             phoneRegex.test(this.customerInfo.telefono) &&
             this.customerInfo.dataRitiro &&
             this.customerInfo.oraRitiro &&
             this.isDayAsportoActive &&
             this.isOraRitiroValida;
    },
    minDate() {
      return new Date().toISOString().split('T')[0]
    },
    totale() {
      return this.ordine.reduce((sum, item) => sum + (item.prezzo * item.quantita), 0)
    },
    isStaff() {
      return this.$route.query.mode === 'staff'
    }
  },
  methods: {
    formatCategoryLabel(id) {
      const predefined = this.predefinedCategories.find(p => p.id === id);
      if (predefined) return this.$t(predefined.label);
      const translation = this.$t(id);
      if (translation !== id) return translation;
      return id.charAt(0).toUpperCase() + id.slice(1);
    },
    getTranslatedName(item) {
      if (!item) return '';
      const locale = this.$i18n.locale;
      if (locale === 'en') return item.nomeEn || item.nome;
      if (locale === 'es') return item.nomeEs || item.nome;
      return item.nome;
    },
    getTranslatedDesc(item) {
      if (!item) return '';
      const locale = this.$i18n.locale;
      if (locale === 'en') return item.descrizioneEn || item.descrizione;
      if (locale === 'es') return item.descrizioneEs || item.descrizione;
      return item.descrizione;
    },
    goBack() {
      if (this.showMenu) {
        this.showMenu = false
      } else {
        if (this.isStaff) {
          this.$router.push({ name: "GestioneAsporto" })
        } else {
          this.$router.push({ name: "Home" })
        }
      }
    },
    proceedToMenu() {
      if (this.isFormValid) {
        this.showMenu = true
      }
    },
    async displayMenu() {
      try {
        const response = await this.axios.post(this.store.path + 'displayMenu')
        if (response.data.messaggio) {
          this.piatti = response.data.messaggio.map(element => ({
            ...element,
            expanded: false,
            nota: ''
          }));
          this.groupPiattiByPortata();
        }
      } catch (error) {
        console.error('Error loading menu:', error)
        this.errorMessage = this.$t('products_load_failed')
      }
    },
    groupPiattiByPortata() {
      const grouped = {};
      this.piatti.forEach(element => {
        const p = element.portata || 'altro';
        if (!grouped[p]) grouped[p] = [];
        grouped[p].push(element);
      });
      this.piattiPerPortata = grouped;
    },
    getGiornoName(dow) {
      if (dow === null || dow === undefined) return '';
      const names = {
        0: 'Domenica',
        1: 'Lunedì',
        2: 'Martedì',
        3: 'Mercoledì',
        4: 'Giovedì',
        5: 'Venerdì',
        6: 'Sabato'
      };
      return names[dow] || '';
    },
    async fetchSettings() {
      try {
        const response = await this.axios.get(this.store.path + 'prenotazioni-setting')
        const setting = response.data.find(s => s.codicesala === 'asporto')
        if (setting) {
          this.asportoSettings = setting
        }
      } catch (error) {
        console.error('Error fetching asporto settings:', error)
      }
    },
    hasItemsInCategory(catId) {
      if (catId === 'antipasti') return this.antipasti.length > 0;
      if (catId === 'primi') return this.primi.length > 0;
      if (catId === 'secondi') return this.secondi.length > 0;
      if (catId === 'contorni') return this.contorni.length > 0;
      if (catId === 'pizza') return this.pizze.length > 0;
      if (catId === 'sushi') return this.sushi.length > 0;
      if (catId === 'dolci') return this.dolci.length > 0;
      if (catId === 'frutta') return this.frutta.length > 0;
      if (catId === 'amari') return this.amari.length > 0;
      if (catId === 'bevande') return this.bevande.length > 0;
      if (catId === 'calice') return this.calice.length > 0;
      if (catId === 'poke') return this.poke.length > 0;
      return false;
    },
    toggleDetails(dish) {
      dish.expanded = !dish.expanded;
    },
    getAllergeni(item) {
      if (!item) return '';
      const locale = this.$i18n.locale;
      if (locale === 'en') return item.allergeniEn || item.allergeni;
      if (locale === 'es') return item.allergeniEs || item.allergeni;
      return item.allergeni;
    },
    addToCart(dish) {
      if ((dish.addProd && dish.addProd.length) || (dish.removeProd && dish.removeProd.length)) {
        this.currentDish = dish
        this.selectedAggiunte = []
        this.selectedSenza = []
        this.notaModal = dish.nota || ''
        this.showVariationModal = true
      } else {
        this.addOrderItem(dish.nome, dish.prezzo, dish.portata, [], [], dish._id || dish.id, dish.nota || '')
        dish.nota = ''
      }
    },
    confirmVariations() {
      this.addOrderItem(this.currentDish.nome, this.currentDish.prezzo, this.currentDish.portata, [...this.selectedAggiunte], [...this.selectedSenza], this.currentDish._id || this.currentDish.id, this.notaModal)
      this.currentDish.nota = ''
      this.showVariationModal = false
    },
    addOrderItem(nome, prezzo, portata, aggiunta, senza, id, nota) {
      const existing = this.ordine.find(item => 
        item.piatto === nome && 
        item.piattoId === id &&
        JSON.stringify(item.aggiunta) === JSON.stringify(aggiunta) && 
        JSON.stringify(item.senza) === JSON.stringify(senza) &&
        item.nota === nota
      )
      
      if (existing) {
        existing.quantita++
      } else {
        this.ordine.push({
          piatto: nome,
          piattoId: id,
          prezzo: prezzo,
          portata: portata,
          quantita: 1,
          aggiunta: aggiunta,
          senza: senza,
          nota: nota
        })
      }
    },
    removeItem(index) {
      this.ordine.splice(index, 1)
    },
    async proceedToPayment() {
      if (this.ordine.length === 0) {
        this.errorMessage = this.$t('carrello_vuoto')
        return
      }

      this.isProcessing = true
      this.errorMessage = ''

      try {
        const response = await this.axios.post(this.store.path + 'creaSessionePagamento', {
          nome: this.customerInfo.nome,
          email: this.customerInfo.email,
          telefono: this.customerInfo.telefono,
          dataRitiro: this.customerInfo.dataRitiro,
          oraRitiro: this.customerInfo.oraRitiro,
          ordinazione: this.ordine,
          totale: Math.round(this.totale * 100)
        })

        if (response.data.sessionUrl) {
          window.location.href = response.data.sessionUrl
        } else {
          this.errorMessage = 'Stripe Error'
        }
      } catch (error) {
        console.error('Payment error:', error)
        this.errorMessage = error.response?.data?.messaggio || 'Payment error'
      } finally {
        this.isProcessing = false
      }
    },
    async proceedToCreateOrder(alreadyPaid) {
      if (this.ordine.length === 0) {
        this.errorMessage = this.$t('carrello_vuoto')
        return
      }

      this.isProcessing = true
      this.errorMessage = ''

      try {
        await this.axios.post(this.store.path + 'creaOrdineAsportoStaff', {
          nome: this.customerInfo.nome,
          email: this.customerInfo.email,
          telefono: this.customerInfo.telefono,
          dataRitiro: this.customerInfo.dataRitiro,
          oraRitiro: this.customerInfo.oraRitiro,
          ordinazione: this.ordine,
          totale: Math.round(this.totale * 100),
          pagato: alreadyPaid,
          headers: {
            authorization: `Bearer ${JSON.parse(localStorage.token)}`
          }
        })

        alert('Success!')
        this.$router.push({ name: 'GestioneAsporto' })
      } catch (error) {
        console.error('Staff order creation error:', error)
        this.errorMessage = error.response?.data?.messaggio || 'Creation error'
      } finally {
        this.isProcessing = false
      }
    }
  },
  beforeMount() {
    this.displayMenu()
  }
}
</script>

<style scoped>
.ordine-asporto {
  height: 100vh;
  overflow-y: auto;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  color: white;
  padding: 20px;
  padding-bottom: 120px;
  box-sizing: border-box;
}

.header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;
}

.header h1 {
  font-size: 1.8rem;
  margin: 0;
  background: linear-gradient(90deg, #00d9ff, #00ff88);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.btn-back {
  color: #00d9ff;
  text-decoration: none;
  font-size: 1rem;
}

/* Customer Form */
.customer-form {
  max-width: 400px;
  margin: 0 auto;
  padding: 30px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  backdrop-filter: blur(10px);
}

.customer-form h2 {
  margin-bottom: 20px;
  color: #00ff88;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #aaa;
}

.form-group input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  box-sizing: border-box;
}

.form-group input::placeholder {
  color: #666;
}

.form-group input:focus {
  outline: none;
  border-color: #00d9ff;
}

.btn-primary {
  width: 100%;
  padding: 14px;
  background: linear-gradient(90deg, #00d9ff, #00ff88);
  border: none;
  border-radius: 8px;
  color: #1a1a2e;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.2s, opacity 0.2s;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary:not(:disabled):hover {
  transform: translateY(-2px);
}

/* Menu Section */
.menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.menu-header h2 {
  color: #00ff88;
}

.cart-badge {
  background: linear-gradient(90deg, #ff6b6b, #ffa500);
  padding: 10px 20px;
  border-radius: 25px;
  cursor: pointer;
  font-weight: bold;
  transition: transform 0.2s;
}

.cart-badge:hover {
  transform: scale(1.05);
}

/* Category Filter Bar */
.category-filter-bar {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 10px 0 20px 0;
  margin-bottom: 20px;
  scrollbar-width: none; /* Firefox */
}

.category-filter-bar::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.cat-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 8px 20px;
  border-radius: 20px;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
}

.cat-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.cat-btn.active {
  background: linear-gradient(90deg, #00d9ff, #00ff88);
  color: #1a1a2e;
  border: none;
  font-weight: bold;
}

/* Cart Overlay */
.cart-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.cart-content {
  background: #1a1a2e;
  padding: 30px;
  border-radius: 16px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
}

.cart-content h3 {
  margin-bottom: 20px;
  color: #00ff88;
}

.cart-empty {
  text-align: center;
  color: #666;
  padding: 20px;
}

.cart-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.cart-item-info {
  display: flex;
  gap: 15px;
  align-items: flex-start;
}

.cart-item-name {
  font-weight: 500;
}

.cart-item-qty {
  color: #00d9ff;
}

.cart-item-price {
  color: #00ff88;
}

.btn-remove {
  background: #ff4444;
  border: none;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
}

.cart-total {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 2px solid #00ff88;
  font-size: 1.3rem;
  text-align: right;
}

.btn-pay {
  width: 100%;
  padding: 16px;
  margin-top: 20px;
  background: linear-gradient(90deg, #6772e5, #9b59b6);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 1.2rem;
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.2s;
}

.btn-pay:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-pay:not(:disabled):hover {
  transform: translateY(-2px);
}

.staff-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
}

.btn-staff-pay {
  background: linear-gradient(90deg, #2ecc71, #27ae60) !important;
}

.btn-staff-pending {
  background: linear-gradient(90deg, #f1c40f, #f39c12) !important;
  color: #1a1a2e !important;
}

.btn-close-cart {
  width: 100%;
  padding: 12px;
  margin-top: 10px;
  background: transparent;
  border: 1px solid #666;
  border-radius: 8px;
  color: #666;
  cursor: pointer;
}

/* Menu Categories */
.category {
  margin-bottom: 40px;
}

.category h3 {
  font-size: 1.5rem;
  margin-bottom: 20px;
  color: #00d9ff;
  border-bottom: 2px solid #00d9ff;
  padding-bottom: 10px;
}

.dishes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.dish-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 20px;
  transition: transform 0.2s, background 0.2s;
}

.dish-card:hover {
  transform: translateY(-5px);
  background: rgba(255, 255, 255, 0.1);
}

.description-container {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
  margin-bottom: 15px;
}

.description-container.expanded {
  max-height: 200px;
}

.allergeni-info {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px dotted rgba(255, 255, 255, 0.2);
  font-size: 0.85rem;
  font-style: italic;
  color: #ff6b6b;
}

.dish-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.btn-info-toggle {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #00d9ff;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-info-toggle:hover {
  background: rgba(0, 217, 255, 0.2);
  border-color: #00d9ff;
}

.dish-card h4 {
  margin: 0 0 10px 0;
  color: white;
}

.dish-card p {
  color: #aaa;
  font-size: 0.9rem;
  margin: 0 0 15px 0;
}

.dish-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.price {
  font-size: 1.2rem;
  font-weight: bold;
  color: #00ff88;
}

.btn-add {
  background: linear-gradient(90deg, #00d9ff, #00ff88);
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  color: #1a1a2e;
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.2s;
}

.btn-add:hover {
  transform: scale(1.05);
}

/* Error Message */
.error-message {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: #ff4444;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  z-index: 999;
}

/* Variation Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
}

.modal-content {
  background: #1a1a2e;
  padding: 30px;
  border-radius: 16px;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid rgba(0, 217, 255, 0.3);
}

.modal-content h3 {
  color: #00ff88;
  margin-top: 0;
  margin-bottom: 20px;
}

.variation-section {
  margin-bottom: 25px;
}

.variation-section h4 {
  color: #00d9ff;
  border-bottom: 1px solid rgba(0, 217, 255, 0.2);
  padding-bottom: 8px;
  margin-bottom: 15px;
}

.variation-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.variation-item {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
  transition: background 0.2s;
}

.variation-item:hover {
  background: rgba(255, 255, 255, 0.1);
}

.variation-item input {
  width: 18px;
  height: 18px;
  accent-color: #00ff88;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

.btn-secondary {
  flex: 1;
  padding: 12px;
  background: transparent;
  border: 1px solid #666;
  border-radius: 8px;
  color: #aaa;
  font-weight: bold;
  cursor: pointer;
}

.cart-item-modifications {
  margin-top: 4px;
  line-height: 1.2;
}

.text-success {
  color: #00ff88;
}

.text-danger {
  color: #ff4444;
}

@media (max-width: 600px) {
  .header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .menu-header {
    flex-direction: column;
    gap: 15px;
  }
  
  .dishes-grid {
    grid-template-columns: 1fr;
  }
}
</style>
